import {
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { Bar, Line, Pie } from "react-chartjs-2";
import "./Admin.css";
import ReviewDialog from "./ReviewDialog";
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const convertDateToDDMMYYYY = (isoDate) => {
  if (isoDate) {
    const date = new Date(isoDate);

    const day = String(date.getUTCDate()).padStart(2, "0");
    const month = String(date.getUTCMonth() + 1).padStart(2, "0"); // Month is 0-indexed
    const year = date.getUTCFullYear();

    return `${day}/${month}/${year}`;
  }
  return "";
};

const AdminDashboard = () => {
  const [sortModel, setSortModel] = useState([
    { field: "Date", sort: "desc" }, // Default sort by Date in descending order
  ]);
  const [caseCategoryData, setCaseCategoryData] = useState({
    labels: [],
    datasets: [],
  });
  const [caseStatusData, setCaseStatusData] = useState({
    labels: [],
    datasets: [],
  });
  const [caseTrendData, setCaseTrendData] = useState({
    labels: [],
    datasets: [],
  });
  const [ageDistributionData, setAgeDistributionData] = useState({
    labels: [],
    datasets: [],
  });
  const [casesByLocationData, setCasesByLocationData] = useState({
    labels: [],
    datasets: [],
  });
  const [statusBreakdownData, setStatusBreakdownData] = useState({
    labels: [],
    datasets: [],
  });
  const [tableData, setTableData] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null); // Track selected row
  const [openDialog, setOpenDialog] = useState(false);
  const [data, setData] = useState([]);

  const [filters, setFilters] = useState({
    year: new Date().getFullYear(),
    month: "all",
    week: "",
  });

  const [originalData, setOriginalData] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const fetchCaseData = async () => {
    const apiBaseUrl =
      process.env.REACT_APP_API_BASE_URL || "https://intelzen-1.onrender.com";
    try {
      const response = await axios.get(`${apiBaseUrl}/api/Mastercases/Master`);
      const fetchedData = response?.data?.data;

      setData(fetchedData);
      setTableData(fetchedData);
      setOriginalData(fetchedData);
      applyFilters(data);
    } catch (error) {
      console.error("Error fetching case data:", error);
    }
  };

  useEffect(() => {
    setFilters({
      year: new Date().getFullYear(),
      month: "all",
      week: "",
    });
    fetchCaseData();
  }, []);

  const resetTable = () => {
    setTableData(originalData); // Reset to original data
  };

  useEffect(() => {
    if (Array.isArray(data) && data.length > 0) {
      const filteredData = applyFilters(data);
      prepareCaseCategoryData(filteredData);
      prepareCaseStatusData(filteredData);
      prepareCaseTrendData(filteredData);
      prepareAgeDistributionData(filteredData);
      prepareCasesByLocationData(filteredData);
      prepareStatusBreakdownData(filteredData);
      setTableData(filteredData);
    }
  }, [filters, data]);

  const applyFilters = (data) => {
    return data.filter((caseItem) => {
      const caseDate = dayjs(caseItem.Date, "YYYY-MM-DD");
      const yearMatch = filters.year ? caseDate.year() === filters.year : true;
      const monthMatch =
        filters.month !== "all"
          ? caseDate.month() + 1 === +filters.month
          : true; // Match all months if "all" is selected
      const weekMatch = filters.week ? caseDate.week() === +filters.week : true;
      return yearMatch && monthMatch && weekMatch;
    });
  };

  const categoryColorMapping = {
    Junk: "#fec001",
    Open: "#ee7d31",
    "Public Safety": "#4472c5",
    "Law Enforcement": "#b1987a",
    Behavioral: "#a5a5a5",
    Unknown: "#d98880",
  };

  const prepareCaseCategoryData = (data) => {
    // Reduce the data to calculate category counts
    const categories = data.reduce((acc, caseItem) => {
      acc[caseItem.Category] = (acc[caseItem.Category] || 0) + 1;
      return acc;
    }, {});

    // Get labels and data from the categories object
    const labels = Object.keys(categories);
    const dataValues = Object.values(categories);

    // Dynamically assign background colors based on categoryColorMapping
    const backgroundColors = labels.map(
      (label) => categoryColorMapping[label] || "#CCCCCC" // Default to gray if no color is defined
    );

    // Set the data for the chart
    setCaseCategoryData({
      labels, // Dynamic labels
      datasets: [
        {
          data: dataValues, // Corresponding values
          backgroundColor: backgroundColors, // Colors based on mapping
        },
      ],
    });
  };

  // const prepareCaseStatusData = (data) => {
  //   const statuses = data.reduce((acc, caseItem) => {
  //     acc[caseItem.Status] = (acc[caseItem.Status] || 0) + 1;
  //     return acc;
  //   }, {});

  //   setCaseStatusData({
  //     labels: Object.keys(statuses),
  //     datasets: [
  //       {
  //         label: "Case Count",
  //         data: Object.values(statuses),
  //         backgroundColor: ["#FF6384", "#36A2EB", "#FFCE56", "#4CAF50"],
  //       },
  //     ],
  //   });
  // };
  const statusColorMapping = {
    Open: "#6596db",
    Closed: "#643699",
    "In Review": "#96d075",
  };

  const prepareCaseStatusData = (data) => {
    // Group data by category and then count each status within the category
    const groupedData = data.reduce((acc, caseItem) => {
      const category = caseItem.Category;
      const status = caseItem.Status;

      if (!acc[category]) {
        acc[category] = { Open: 0, Closed: 0, "In Review": 0, Resolved: 0 };
      }

      acc[category][status] = (acc[category][status] || 0) + 1;

      return acc;
    }, {});

    // Extract labels and datasets
    const labels = Object.keys(groupedData); // Categories for X-axis
    const openData = labels.map((category) => groupedData[category].Open || 0);
    const closedData = labels.map(
      (category) => groupedData[category].Closed || 0
    );
    const inProgressData = labels.map(
      (category) => groupedData[category]["In Review"] || 0
    );
    const resolvedData = labels.map(
      (category) => groupedData[category].Resolved || 0
    );

    setCaseStatusData({
      labels, // X-axis labels (categories)
      datasets: [
        {
          label: "Open",
          data: openData,
          backgroundColor: statusColorMapping.Open,
        },
        {
          label: "Closed",
          data: closedData,
          backgroundColor: statusColorMapping.Closed,
        },
        {
          label: "In Review",
          data: inProgressData,
          backgroundColor: statusColorMapping["In Review"],
        },
        {
          label: "Resolved",
          data: resolvedData,
          backgroundColor: statusColorMapping.Resolved,
        },
      ],
    });
  };

  // const prepareCaseTrendData = (data) => {
  //   const trends = data.reduce((acc, caseItem) => {
  //     const date = dayjs(caseItem.Date, "DD/MM/YYYY").format("YYYY-MM");
  //     acc[date] = (acc[date] || 0) + 1;
  //     return acc;
  //   }, {});

  //   setCaseTrendData({
  //     labels: Object.keys(trends).sort(),
  //     datasets: [
  //       {
  //         label: "Cases Over Time",
  //         data: Object.values(trends).sort(),
  //         fill: false,
  //         borderColor: "#36A2EB",
  //       },
  //     ],
  //   });
  // };

  const prepareCaseTrendData = (data) => {
    const trends = data.reduce((acc, caseItem) => {
      const month = dayjs(caseItem.Date, "YYYY-MM-DD").format("YYYY-MM");
      const status = caseItem.Status;

      if (!acc[month]) acc[month] = { Open: 0, Closed: 0 };
      if (status === "Open") acc[month].Open += 1;
      if (status === "Closed") acc[month].Closed += 1;

      return acc;
    }, {});

    const months = Object.keys(trends).sort();
    const openCases = months.map((month) => trends[month].Open || 0);
    const closedCases = months.map((month) => trends[month].Closed || 0);

    setCaseTrendData({
      labels: months,
      datasets: [
        {
          label: "Open Cases",
          data: openCases,
          borderColor: "#36A2EB",
          fill: false,
          tension: 0.3,
        },
        {
          label: "Closed Cases",
          data: closedCases,
          borderColor: "#FF6384",
          fill: false,
          tension: 0.3,
        },
      ],
    });
  };

  const prepareAgeDistributionData = (data) => {
    const currentDate = dayjs();
    const ages = data.map((caseItem) => {
      const submittedDate = dayjs(caseItem.Date, "DD/MM/YYYY");
      return currentDate.diff(submittedDate, "day");
    });

    const ageBuckets = ages.reduce((acc, age) => {
      const bucket = Math.floor(age / 10) * 10;
      acc[bucket] = (acc[bucket] || 0) + 1;
      return acc;
    }, {});

    setAgeDistributionData({
      labels: Object.keys(ageBuckets).map(
        (bucket) => `${bucket}-${+bucket + 9} days`
      ),
      datasets: [
        {
          label: "Case Age Distribution",
          data: Object.values(ageBuckets),
          backgroundColor: "#FFCE56",
        },
      ],
    });
  };

  const prepareCasesByLocationData = (data) => {
    const locationData = data.reduce((acc, caseItem) => {
      const { Location, Status } = caseItem;

      if (!acc[Location]) {
        acc[Location] = { Open: 0, Closed: 0, Resolved: 0, "In Review": 0 };
      }

      if (Status === "Open") {
        acc[Location].Open += 1;
      } else if (Status === "Closed") {
        acc[Location].Closed += 1;
      } else if (Status === "Resolved") {
        acc[Location].Resolved += 1;
      } else if (Status === "In Review") {
        acc[Location]["In Review"] += 1;
      }

      return acc;
    }, {});

    const labels = Object.keys(locationData);
    const openData = labels.map((location) => locationData[location].Open);
    const closedData = labels.map((location) => locationData[location].Closed);
    const resolvedData = labels.map(
      (location) => locationData[location].Resolved
    );
    const inReviewData = labels.map(
      (location) => locationData[location]["In Review"]
    );

    setCasesByLocationData({
      labels,
      datasets: [
        {
          label: "Open Cases",
          data: openData,
          backgroundColor: "#36A2EB",
        },
        {
          label: "Closed Cases",
          data: closedData,
          backgroundColor: "#FF6384",
        },
        {
          label: "Resolved Cases",
          data: resolvedData,
          backgroundColor: "#4CAF50",
        },
        {
          label: "In Review Cases",
          data: inReviewData,
          backgroundColor: "#FFCE56",
        },
      ],
    });
  };

  const prepareStatusBreakdownData = (data) => {
    const statusCounts = data.reduce(
      (acc, caseItem) => {
        if (caseItem.Status === "Open") acc.open += 1;
        else if (caseItem.Status === "Closed") acc.closed += 1;
        else if (caseItem.Status === "Resolved") acc.resolved += 1;
        else if (caseItem.Status === "In Review") acc.inReview += 1;
        return acc;
      },
      { open: 0, closed: 0, resolved: 0, inReview: 0 }
    );

    setStatusBreakdownData({
      labels: ["Open", "Closed", "Resolved", "In Review"],
      datasets: [
        {
          label: "Case Status Breakdown",
          data: [
            statusCounts.open,
            statusCounts.closed,
            statusCounts.resolved,
            statusCounts.inReview,
          ],
          backgroundColor: ["#36A2EB", "#FF6384", "#4CAF50", "#FFCE56"],
        },
      ],
    });
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const columns = [
    {
      field: "Date",
      headerName: "Date",
      flex: 1,
      filterable: true,
      minWidth: 120,
    },
    {
      field: "User ID",
      headerName: "User ID",
      flex: 1,
      filterable: true,
      minWidth: 120,
    },
    {
      field: "Case ID",
      headerName: "Case ID",
      flex: 1,
      filterable: true,
      minWidth: 120,
    },
    {
      field: "Location",
      headerName: "Location",
      flex: 1,
      filterable: true,
      minWidth: 120,
    },
    {
      field: "Category",
      headerName: "Category",
      flex: 1,
      filterable: true,
      minWidth: 120,
    },
    {
      field: "Priority",
      headerName: "Priority",
      flex: 1,
      filterable: true,
      minWidth: 120,
    },
    {
      field: "Status",
      headerName: "Status",
      flex: 1,
      filterable: true,
      minWidth: 120,
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      minWidth: 80,
      renderCell: (params) => (
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleRowClick(params.row)}
        >
          View Details
        </Button>
      ),
    },
  ];

  const defaultSortModel = [
    {
      field: "Date",
      sort: "desc", // 'desc' sorts the date in descending order
    },
  ];

  const handleSortModelChange = (newSortModel) => {
    if (newSortModel.length > 0) {
      const { field, sort } = newSortModel[0];

      const sortedData = [...tableData].sort((a, b) => {
        if (a[field] < b[field]) return sort === "asc" ? -1 : 1;
        if (a[field] > b[field]) return sort === "asc" ? 1 : -1;
        return 0;
      });

      setSortModel(newSortModel);
      setTableData(sortedData);
    }
  };

  const handleRowClick = (row) => {
    setSelectedRow(row);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedRow(null);
  };

  const handleSave = (updatedData) => {
    const updatedTable = tableData.map((row) =>
      row["Case ID"] === updatedData["Case ID"] ? updatedData : row
    );
    setTableData(updatedTable);
    handleCloseDialog();
  };
  const handleChartClick = (event, elements, chart) => {
    if (elements.length > 0) {
      const index = elements[0].index; // Get the index of the clicked element
      const category = chart.data.labels[index]; // Use the index to find the category

      // Filter table data based on the clicked category
      const filteredData = data.filter((item) => item.Category === category);
      setTableData(filteredData); // Update the table data
    }
  };
  const handleLocationChartClick = (event, elements, chart) => {
    if (elements.length > 0) {
      const index = elements[0].index; // Get the index of the clicked bar
      const location = chart.data.labels[index]; // Use the index to find the location

      // Filter table data based on the selected location
      const filteredData = data.filter((item) => item.Location === location);
      setTableData(filteredData); // Update the table data
    }
  };

  return (
    <div className="dashboard">
      <h1 className="dashboard-title">Admin Dashboard</h1>
      <div
        className="filters"
        style={{ display: "flex", justifyContent: "flex-end", gap: "1rem" }}
      >
        <Grid container spacing={2} justifyContent="flex-end">
          {/* Year Dropdown */}
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <FormControl fullWidth>
              <InputLabel id="year-select-label">Year</InputLabel>
              <Select
                labelId="year-select-label"
                id="year-select"
                value={filters.year}
                label="Year"
                onChange={(e) => handleFilterChange("year", +e.target.value)}
              >
                <MenuItem value="" disabled>
                  Select Year
                </MenuItem>
                {Array.from({ length: 10 }, (_, i) => {
                  const year = new Date().getFullYear() - i;
                  return (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>

          {/* Month Dropdown */}
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <FormControl fullWidth>
              <InputLabel id="month-select-label">Month</InputLabel>
              <Select
                labelId="month-select-label"
                id="month-select"
                value={filters.month || "all"}
                label="Month"
                onChange={(e) => handleFilterChange("month", e.target.value)}
              >
                <MenuItem value="all">All Months</MenuItem>
                {Array.from({ length: 12 }, (_, i) => (
                  <MenuItem key={i + 1} value={i + 1}>
                    {dayjs().month(i).format("MMMM")}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </div>

      <div className="card-container">
        <div className="card">
          <h2>Case Distribution by Category</h2>
          {caseCategoryData.labels.length > 0 ? (
            <div
              style={{
                width: "100%",
                height: "100%",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Pie
                data={caseCategoryData}
                options={{
                  onClick: (event, elements, chart) =>
                    handleChartClick(event, elements, chart),
                  plugins: {
                    legend: { position: "top" },
                  },
                }}
              />
            </div>
          ) : (
            <p>No data available</p>
          )}
        </div>
        <div className="card">
          <h2>Case Status Overview</h2>
          {caseStatusData.labels.length > 0 ? (
            <div style={{ height: "90%", width: "100%" }}>
              <Bar
                data={caseStatusData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { position: "top" } },
                  scales: {
                    x: { stacked: true },
                    y: { stacked: true },
                  },
                  onClick: (event, elements, chart) =>
                    handleChartClick(event, elements, chart),
                }}
              />
            </div>
          ) : (
            <p>No data available</p>
          )}
        </div>
        {/* <div className="card">
          <h2>Trend Over Time</h2>
          {caseTrendData.labels.length > 0 ? (
            <Line
              data={caseTrendData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: "top",
                  },
                },
                scales: {
                  x: {
                    title: {
                      display: true,
                      text: "Months",
                    },
                  },
                  y: {
                    title: {
                      display: true,
                      text: "Number of Cases",
                    },
                    beginAtZero: true,
                  },
                },
              }}
            />
          ) : (
            <p>No data available</p>
          )}
        </div> */}
        {/* <div className="card">
          <h2>Age of Cases</h2>
          {ageDistributionData.labels.length > 0 ? (
            <Bar data={ageDistributionData} />
          ) : (
            <p>No data available</p>
          )}
        </div> */}
      </div>
      <div className="full-width-card">
        <h2>Trend Over Time</h2>
        {caseTrendData.labels.length > 0 ? (
          <div style={{ width: "100%", height: "85%" }}>
            <Line
              data={caseTrendData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: "top",
                  },
                },
                scales: {
                  x: {
                    title: {
                      display: true,
                      text: "Months",
                    },
                  },
                  y: {
                    title: {
                      display: true,
                      text: "Number of Cases",
                    },
                    beginAtZero: true,
                  },
                },
              }}
            />
          </div>
        ) : (
          <p>No data available</p>
        )}
        {/* <h2>Cases by Location</h2> */}
        {/* {casesByLocationData.labels.length > 0 ? (
          <div className="chart-container" style={{ height: "90%" }}>
            <Bar
              data={casesByLocationData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { position: "top" } },
                scales: {
                  x: { stacked: true },
                  y: { stacked: true },
                },
                onClick: (event, elements, chart) =>
                  handleLocationChartClick(event, elements, chart),
              }}
            />
          </div>
        ) : (
          <p>No data available</p>
        )} */}
      </div>

      <div className="table-container">
        <h2>All Issues</h2>
        <DataGrid
          rows={tableData}
          columns={columns}
          pageSizeOptions={[10, 25, 50]}
          pagination
          paginationModel={paginationModel}
          onPaginationModelChange={(model) => setPaginationModel(model)}
          autoHeight
          getRowId={(row) => `${row["Case ID"]}-${row["User ID"]}`}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
        />
      </div>
      {selectedRow && (
        <ReviewDialog
          open={openDialog}
          onClose={handleCloseDialog}
          rowData={selectedRow}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

export default AdminDashboard;
